import { useGLTF } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { SkeletonUtils } from "three-stdlib";

import { useTheme } from "../../components/ui-v2";
import { useModelViewerSettings } from "./useModelViewerSettings";

const MODEL_ROTATION_SPEED = 0.0075;
const FRUSTRUM_SCALE = 0.6;

const resizeScene = (
  scene: THREE.Group<THREE.Object3DEventMap>,
  maxExtent: number,
) => {
  // Pose the whole skeleton before measuring. Box3.setFromObject sizes a skinned
  // mesh from its posed vertices, but only reads each bone's current matrixWorld —
  // on a freshly cloned scene those are stale, and the skinned bounding box is
  // computed once and cached. If the mesh node is traversed before its bone
  // hierarchy (as in rigged glTF exports where the mesh precedes the skeleton),
  // the box is cached from an unposed skeleton and the model ends up mis-scaled
  // and off-centre. Forcing the update first, and clearing the cache, avoids that.
  scene.updateMatrixWorld(true);
  scene.traverse((child) => {
    if (child instanceof THREE.SkinnedMesh) {
      // three types boundingBox as non-nullable, but assigning null is the documented way to
      // invalidate the cached box so setFromObject recomputes it from the posed skeleton.
      (child as { boundingBox: THREE.Box3 | null }).boundingBox = null;
    }
  });

  const bbox = new THREE.Box3().setFromObject(scene);
  if (bbox.isEmpty()) {
    return;
  }

  const size = bbox.getSize(new THREE.Vector3());
  const center = bbox.getCenter(new THREE.Vector3());

  // Normalize the largest axis to maxExtent, then recenter on the origin (the
  // camera's target) so off-origin rigs stay framed. Center is scaled by the
  // same factor since the scale is applied about the scene's local origin.
  const maxAxis = Math.max(size.x, size.y, size.z);
  const factor = maxExtent / maxAxis;
  scene.scale.multiplyScalar(factor);
  scene.position.sub(center.multiplyScalar(factor));
};

interface ModelProps {
  url: string;
  rotate: boolean;
  showTextured: boolean;
  onModelLoaded?: (model: THREE.Group) => void;
}

export const Model = (props: ModelProps) => {
  const { url, rotate, showTextured, onModelLoaded } = props;

  const { materialMode } = useModelViewerSettings();

  const theme = useTheme();

  const { scene } = useGLTF(url);
  const modelRef = useRef<THREE.Group>(null);

  const clonedScene = useMemo(() => {
    const cloned = SkeletonUtils.clone(scene) as THREE.Group;
    resizeScene(cloned, FRUSTRUM_SCALE);
    return cloned;
  }, [scene]);

  const originalMaterials = useRef<
    Map<THREE.Mesh, THREE.Material | THREE.Material[]>
  >(new Map());

  useEffect(() => {
    originalMaterials.current.clear();

    // Store original materials on first render
    clonedScene.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        originalMaterials.current.set(child, child.material);
      }
    });

    // Notify parent that model is loaded
    onModelLoaded?.(clonedScene);
  }, [clonedScene, onModelLoaded]);

  useFrame(() => {
    if (modelRef.current && rotate) {
      modelRef.current.rotation.y += MODEL_ROTATION_SPEED;

      // For skinned meshes, we need to update the world matrix
      // This ensures bone transformations are recalculated
      modelRef.current.updateMatrixWorld(true);
    }
  });

  useEffect(() => {
    clonedScene.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (materialMode === "textured" || showTextured) {
          // Apply original material
          child.material =
            originalMaterials.current.get(child) || child.material;
          child.material.metalness = 0;
        } else if (materialMode === "plain") {
          // Apply standard material without textures
          child.material = new THREE.MeshStandardMaterial({
            color: theme.palette.secondary[3],
          });
        } else if (materialMode === "wireframe") {
          // Only show wireframes
          child.material = new THREE.MeshBasicMaterial({
            color: theme.palette.secondary[3],
            wireframe: true,
          });
        }
      }
    });
  }, [clonedScene, materialMode, showTextured, theme]);

  return <primitive ref={modelRef} object={clonedScene} />;
};
